import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MessageSquareQuote,
  MessagesSquare,
  MessageCircle,
  MessageSquare,
  Instagram,
  Globe,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
  Hash,
  Search,
  ChevronDown,
  Zap,
  X,
  CornerDownLeft,
} from "lucide-react";
import HelpdeskSuiteLayout from "../../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import useAuth from "../../../hooks/useAuth";
import useIsMobile from "../../../hooks/useIsMobile";
import { roleRoutes } from "../../../utils/roles";
import { cn } from "../../../utils/cn";
import { showToast } from "../../../utils/showToast";
// El contrato original nombraba `formatWhatsAppText(text)`; el módulo publicado
// expone la misma firma (texto → JSX) como `renderWhatsApp`. `toPlainText` sirve
// para la línea resumen de la tarjeta y para buscar, donde los delimitadores
// (*_~) serían ruido.
import { renderWhatsApp, toPlainText } from "../../../utils/whatsappFormat";
import {
  getQuickReplies,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
  isEndpointMissing,
} from "../../../services/aiChatService";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Card } from "../../../components/ui/Card";
import { Stat } from "../../../components/ui/Stat";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { Badge } from "../../../components/ui/Badge";
import { Switch } from "../../../components/ui/Switch";
import { Modal } from "../../../components/ui/Modal";
import { EmptyState } from "../../../components/ui/EmptyState";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { Skeleton } from "../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";

// Configuración de respuestas rápidas de la mesa de ayuda: el catálogo de
// plantillas que los agentes insertan en el composer escribiendo su atajo.
// Vive en /admin/helpdesk/quick-replies, dentro de la suite del helpdesk.
//
// Presentación: tarjetas expandibles (no tabla). El catálogo se lee de un
// vistazo —canal, categoría, estado, atajo— y solo al abrir una tarjeta
// aparecen el mensaje completo y sus acciones. Es el patrón de la vista de
// comunicaciones del mockup mu.
//
// El microservicio de quick-replies todavía NO está desplegado: todas las
// llamadas pueden fallar con 404/405. Por eso la vista distingue tres fallos
// distintos —backend ausente (isEndpointMissing), error real, y lista vacía—
// y sigue siendo navegable en el primer caso.

const BODY_MAX = 4096;
const TITLE_MAX = 120;
const CATEGORY_MAX = 40;

// El atajo se teclea en el composer, por eso se restringe a un token sin
// espacios ni mayúsculas: evita colisiones al parsear lo que escribe el agente.
const SHORTCUT_RE = /^\/[a-z0-9_-]{1,30}$/;

// Canales soportados. `value: ""` = la respuesta aplica a todos los canales.
const CHANNEL_TONES = {
  whatsapp: "success",
  messenger: "info",
  instagram: "cc",
  portal: "neutral",
};

// Ícono por canal. El cuadro que lo contiene va en gris (bg-bg-muted): el color
// se reserva para el badge de canal y el de estado, que son la información.
const CHANNEL_ICONS = {
  whatsapp: MessageCircle,
  messenger: MessageSquare,
  instagram: Instagram,
  portal: Globe,
  "": MessagesSquare,
};

// Orden de los chips de filtro: primero las que sirven a cualquier canal.
const CHANNEL_ORDER = ["", "whatsapp", "messenger", "instagram", "portal"];

// Evolution —el WhatsApp no oficial, canal principal del producto— llega como
// 'whatsapp_unofficial' en las conversaciones, pero el catálogo solo guarda
// 'whatsapp'. Se tratan como el mismo canal al etiquetar y al previsualizar,
// igual que hacen HelpdeskRail y el filtro del picker.
function normalizeChannel(value) {
  return value === "whatsapp_unofficial" ? "whatsapp" : value || "";
}

// Solo los canales de WhatsApp interpretan el marcado (*negrita*, _cursiva_…):
// mismo criterio que WHATSAPP_CHANNELS en ConversationDetail. En el resto los
// delimitadores viajan literales, así que la vista previa no debe prometerlos.
function supportsWhatsAppFormat(channel) {
  const normalized = normalizeChannel(channel);
  return !normalized || normalized === "whatsapp";
}

function channelLabel(value, t) {
  switch (normalizeChannel(value)) {
    case "whatsapp":
      return "WhatsApp";
    case "messenger":
      return "Messenger";
    case "instagram":
      return "Instagram";
    case "portal":
      return t("Portal");
    default:
      return t("Todos");
  }
}

function channelIconFor(value) {
  return CHANNEL_ICONS[normalizeChannel(value)] || MessagesSquare;
}

// Normalización para buscar: sin mayúsculas ni tildes, para que "horario" y
// "Horário" encuentren lo mismo.
function normalizeQuery(value) {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const EMPTY_DRAFT = {
  title: "",
  body: "",
  shortcut: "",
  category: "",
  channel: "",
  active: true,
};

// Validación pura: se recalcula en cada render para que el modal pueda mostrar
// errores en vivo sin duplicar estado.
function validateDraft(draft, t) {
  const errors = {};
  const title = (draft.title || "").trim();
  const body = draft.body || "";
  const shortcut = (draft.shortcut || "").trim();

  if (!title) {
    errors.title = t("El título es obligatorio.");
  } else if (title.length > TITLE_MAX) {
    errors.title = t("El título es demasiado largo.");
  }

  if (!body.trim()) {
    errors.body = t("El cuerpo del mensaje es obligatorio.");
  } else if (body.length > BODY_MAX) {
    errors.body = t("El cuerpo supera el máximo permitido.");
  }

  if (shortcut && !SHORTCUT_RE.test(shortcut)) {
    errors.shortcut = t(
      "El atajo debe empezar con / y seguir con 1 a 30 caracteres: minúsculas, números, guion o guion bajo. Ejemplo: /horario-atencion",
    );
  }

  if ((draft.category || "").trim().length > CATEGORY_MAX) {
    errors.category = t("La categoría es demasiado larga.");
  }

  return errors;
}

// Orden estable del catálogo: sort_order asc y, a igualdad, título. Las filas
// sin sort_order caen al final en vez de mezclarse al principio como 0.
function sortReplies(list) {
  return [...list].sort((a, b) => {
    const ao = Number.isFinite(a?.sort_order) ? a.sort_order : Number.MAX_SAFE_INTEGER;
    const bo = Number.isFinite(b?.sort_order) ? b.sort_order : Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return (a?.title || "").localeCompare(b?.title || "");
  });
}

// ─── Vista previa con formato WhatsApp ───────────────────────────────────────
function BodyPreview({ body, channel, t }) {
  const trimmed = (body || "").trim();
  const format = supportsWhatsAppFormat(channel);
  return (
    <div className="rounded-lg border border-border bg-bg-subtle p-3">
      <div className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider mb-2">
        {t("Vista previa")}
      </div>
      {trimmed ? (
        // Misma receta que la burbuja saliente del agente en ConversationDetail,
        // para que la vista previa sea idéntica a lo que se verá en el chat.
        <div className="max-w-[80%] ml-auto rounded-lg rounded-br-md hd-brand-solid bg-brand-500 text-white px-3 py-2 text-sm whitespace-pre-wrap break-words">
          {renderWhatsApp(trimmed, { format })}
        </div>
      ) : (
        <p className="text-sm text-fg-subtle">
          {t("Escribe el mensaje para ver cómo se verá en el chat.")}
        </p>
      )}
    </div>
  );
}

// ─── Tarjeta expandible ──────────────────────────────────────────────────────
// El encabezado completo es el disparador (un solo <button>): por eso dentro no
// hay ningún control anidado —los badges son texto— y todas las acciones viven
// en el cuerpo desplegado.
function QuickReplyCard({
  reply,
  index,
  total,
  expanded,
  onToggle,
  busy,
  reorderLocked,
  onEdit,
  onMove,
  onToggleActive,
  onDelete,
  t,
}) {
  const Icon = channelIconFor(reply.channel);
  const active = reply.active !== false;
  const panelId = `qr-panel-${reply.id}`;
  const summary = toPlainText(reply.body);
  // A6 · En móvil el título va PRIMERO (los chips encima invertían la
  // jerarquía de la fila). Se reordena el DOM y no con `order-*`: con
  // `order` el orden visual y el de lectura divergen, que es exactamente la
  // técnica F1 que WCAG 1.3.2 declara fallo. En ≥768px el orden es el de
  // siempre (chips y luego título), sin una sola clase nueva.
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-bg overflow-hidden",
        "transition-colors duration-150 ease-out motion-reduce:transition-none",
        "hover:border-brand-500 focus-within:border-brand-500",
        busy && "opacity-60",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={cn(
          "w-full text-left p-4 flex items-start gap-3",
          "hover:bg-bg-muted/40 transition-colors duration-150 ease-out motion-reduce:transition-none",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500",
        )}
      >
        <span className="w-10 h-10 rounded-lg bg-bg-muted text-fg-muted inline-flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" aria-hidden="true" />
        </span>

        <span className="flex-1 min-w-0">
          {(() => {
            const chips = (
              <span key="chips" className="flex flex-wrap items-center gap-1.5 mb-1">
                <Badge tone={CHANNEL_TONES[normalizeChannel(reply.channel)] || "neutral"}>
                  {channelLabel(reply.channel, t)}
                </Badge>
                {reply.category && (
                  <Badge tone="neutral">
                    <Hash className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    {reply.category}
                  </Badge>
                )}
                {/* El estado nunca va solo por color: el punto acompaña al texto. */}
                <Badge tone={active ? "success" : "neutral"} dot>
                  {active ? t("Activa") : t("Inactiva")}
                </Badge>
              </span>
            );
            const title = (
              <span
                key="title"
                className={cn(
                  "block text-sm font-semibold text-fg truncate",
                  isMobile && "mb-1",
                )}
              >
                {reply.title}
              </span>
            );
            return isMobile ? [title, chips] : [chips, title];
          })()}
          <span className="block text-xs text-fg-muted truncate mt-0.5">{summary}</span>
        </span>

        <span className="flex items-center gap-2 shrink-0 pl-1">
          {reply.shortcut && (
            <span className="hidden sm:inline font-mono text-xs text-fg-muted">
              {reply.shortcut}
            </span>
          )}
          <ChevronDown
            className={cn(
              "w-4 h-4 text-fg-subtle transition-transform duration-200 ease-out motion-reduce:transition-none",
              expanded && "rotate-180",
            )}
            aria-hidden="true"
          />
        </span>
      </button>

      {expanded && (
        <div
          id={panelId}
          className="border-t border-border bg-bg-muted/30 p-4 space-y-3 animate-fade-in motion-reduce:animate-none"
        >
          <BodyPreview body={reply.body} channel={reply.channel} t={t} />

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
            <span className="text-[11px] font-semibold uppercase tracking-wider">
              {t("Atajo")}
            </span>
            {reply.shortcut ? (
              <span className="font-mono text-xs text-fg rounded bg-bg border border-border px-1.5 py-0.5">
                {reply.shortcut}
              </span>
            ) : (
              <span className="text-fg-subtle">{t("Sin atajo")}</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
            <span className="inline-flex items-center gap-2 text-sm text-fg-muted">
              <Switch
                size="sm"
                checked={active}
                disabled={busy}
                onChange={(next) => onToggleActive(reply, next)}
                ariaLabel={
                  active ? t("Desactivar respuesta rápida") : t("Activar respuesta rápida")
                }
              />
              {active ? t("Activa") : t("Inactiva")}
            </span>

            <span className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="px-2"
                disabled={index === 0 || busy || reorderLocked}
                onClick={() => onMove(index, -1)}
                aria-label={t("Subir en el orden")}
                title={reorderLocked ? t("Quita los filtros para reordenar.") : t("Subir en el orden")}
              >
                <ArrowUp className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="px-2"
                disabled={index === total - 1 || busy || reorderLocked}
                onClick={() => onMove(index, 1)}
                aria-label={t("Bajar en el orden")}
                title={reorderLocked ? t("Quita los filtros para reordenar.") : t("Bajar en el orden")}
              >
                <ArrowDown className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(reply)}
                iconLeft={<Pencil className="w-4 h-4" aria-hidden="true" />}
              >
                {t("Editar")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-status-error hover:bg-status-error-soft"
                onClick={() => onDelete(reply)}
                iconLeft={<Trash2 className="w-4 h-4" aria-hidden="true" />}
              >
                {t("Eliminar")}
              </Button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal de crear / editar ─────────────────────────────────────────────────
function QuickReplyFormModal({ open, mode, draft, onChange, onSubmit, onClose, saving, backendMissing }) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState({});
  const errors = useMemo(() => validateDraft(draft, t), [draft, t]);
  const hasErrors = Object.keys(errors).length > 0;

  // Cada apertura arranca sin errores visibles: se muestran a medida que el
  // usuario toca cada campo (o al intentar guardar).
  useEffect(() => {
    if (open) setTouched({});
  }, [open]);

  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));
  const errorFor = (field) => (touched[field] ? errors[field] : undefined);

  const handleSubmit = () => {
    setTouched({ title: true, body: true, shortcut: true, category: true });
    if (hasErrors) return;
    onSubmit();
  };

  const bodyLength = (draft.body || "").length;
  const bodyOver = bodyLength > BODY_MAX;
  const formatSupported = supportsWhatsAppFormat(draft.channel);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={mode === "edit" ? t("Editar respuesta rápida") : t("Nueva respuesta rápida")}
      description={t(
        "Los agentes la insertan en el chat escribiendo su atajo, sin salir de la conversación.",
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          {/* Sin `disabled` a propósito: si el botón no responde, el usuario no
              descubre QUÉ falta. Al pulsar se marcan todos los campos y se
              revelan los errores. */}
          <Button className="hd-brand-solid" onClick={handleSubmit} loading={saving}>
            {mode === "edit" ? t("Guardar cambios") : t("Crear respuesta")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {backendMissing && (
          <PendingBackendNotice>
            {t(
              "Puedes revisar el formulario, pero el guardado está en construcción.",
            )}
          </PendingBackendNotice>
        )}

        <div className="space-y-1.5">
          <FieldLabel className="hd-field-label" htmlFor="qr-title">{t("Título")}</FieldLabel>
          <Input
            id="qr-title"
            value={draft.title}
            maxLength={TITLE_MAX}
            invalid={!!errorFor("title")}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
            onBlur={() => markTouched("title")}
            placeholder={t("Ej: Horario de atención")}
            aria-describedby={errorFor("title") ? "qr-title-error" : undefined}
          />
          {errorFor("title") ? (
            <p id="qr-title-error" className="text-xs text-status-error">
              {errorFor("title")}
            </p>
          ) : (
            <p className="text-xs text-fg-muted">
              {t("Es el nombre con el que el agente la encuentra en la lista.")}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <FieldLabel className="hd-field-label" htmlFor="qr-body">{t("Mensaje")}</FieldLabel>
            <span
              className={cn(
                "text-xs tabular-nums",
                bodyOver ? "text-status-error font-semibold" : "text-fg-subtle",
              )}
            >
              {bodyLength} / {BODY_MAX}
            </span>
          </div>
          <textarea
            id="qr-body"
            rows={6}
            value={draft.body}
            onChange={(e) => onChange({ ...draft, body: e.target.value })}
            onBlur={() => markTouched("body")}
            placeholder={t("Escribe el mensaje que se enviará al cliente…")}
            aria-describedby={errorFor("body") ? "qr-body-error" : undefined}
            className={cn(
              "w-full rounded-lg border bg-bg px-3 py-2 text-sm text-fg resize-y",
              "placeholder:text-fg-subtle",
              "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
              errorFor("body") || bodyOver ? "border-status-error" : "border-border",
            )}
          />
          {errorFor("body") ? (
            <p id="qr-body-error" className="text-xs text-status-error">
              {errorFor("body")}
            </p>
          ) : (
            <p className="text-xs text-fg-muted">
              {formatSupported
                ? t("Admite el formato de WhatsApp: *negrita*, _cursiva_, ~tachado~ y ```código```.")
                : t(
                    "Este canal no interpreta el formato de WhatsApp: los asteriscos y guiones bajos se enviarán tal cual.",
                  )}
            </p>
          )}
        </div>

        <BodyPreview body={draft.body} channel={draft.channel} t={t} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <FieldLabel className="hd-field-label" htmlFor="qr-shortcut">{t("Atajo")}</FieldLabel>
            <Input
              id="qr-shortcut"
              value={draft.shortcut}
              invalid={!!errorFor("shortcut")}
              onChange={(e) =>
                onChange({ ...draft, shortcut: e.target.value.toLowerCase().replace(/\s+/g, "") })
              }
              onBlur={() => markTouched("shortcut")}
              placeholder="/horario-atencion"
              className="font-mono"
              aria-describedby={errorFor("shortcut") ? "qr-shortcut-error" : undefined}
            />
            {errorFor("shortcut") ? (
              <p id="qr-shortcut-error" className="text-xs text-status-error">
                {errorFor("shortcut")}
              </p>
            ) : (
              <p className="text-xs text-fg-muted">
                {t("Opcional. El agente lo escribe en el chat para insertar el mensaje.")}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <FieldLabel className="hd-field-label" htmlFor="qr-category">{t("Categoría")}</FieldLabel>
            <Input
              id="qr-category"
              value={draft.category}
              maxLength={CATEGORY_MAX}
              invalid={!!errorFor("category")}
              onChange={(e) => onChange({ ...draft, category: e.target.value })}
              onBlur={() => markTouched("category")}
              placeholder={t("Ej: Entregas")}
            />
            {errorFor("category") ? (
              <p className="text-xs text-status-error">{errorFor("category")}</p>
            ) : (
              <p className="text-xs text-fg-muted">
                {t("Opcional. Agrupa respuestas relacionadas.")}
              </p>
            )}
          </div>
        </div>

        {/* El `label` propio de Select se renderiza en sentence case, distinto a
            los FieldLabel del resto del formulario; se etiqueta a mano para que
            los cinco campos del modal se lean como una sola columna. */}
        <div className="space-y-1.5">
          <FieldLabel className="hd-field-label" htmlFor="qr-channel">{t("Canal")}</FieldLabel>
          <Select
            id="qr-channel"
            value={draft.channel}
            onChange={(e) => onChange({ ...draft, channel: e.target.value })}
            hint={t("Limita en qué canal aparece la respuesta.")}
            options={[
              { value: "", label: t("Todos los canales") },
              { value: "whatsapp", label: "WhatsApp" },
              { value: "messenger", label: "Messenger" },
              { value: "instagram", label: "Instagram" },
              { value: "portal", label: t("Portal") },
            ]}
          />
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-border bg-bg-muted p-3">
          <Switch
            id="qr-active"
            checked={!!draft.active}
            onChange={(next) => onChange({ ...draft, active: next })}
            ariaLabel={t("Activa")}
          />
          <div className="min-w-0">
            <label htmlFor="qr-active" className="text-sm font-medium text-fg cursor-pointer">
              {t("Activa")}
            </label>
            <p className="text-xs text-fg-muted mt-0.5">
              {t("Si la desactivas, deja de ofrecerse a los agentes pero no se borra.")}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Vista ───────────────────────────────────────────────────────────────────
export default function QuickRepliesSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Mismo criterio de permiso que ConversationDetail (isSupervisor): la
  // configuración del catálogo es de supervisión, no de agente de piso.
  const isSupervisor =
    user?.role === roleRoutes.supervisor ||
    user?.role === roleRoutes.admin ||
    user?.role === roleRoutes.superadmin ||
    user?.role === roleRoutes.systemSuperadmin;

  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Presentación: qué tarjeta está abierta y cómo está filtrado el catálogo.
  const [expandedId, setExpandedId] = useState(null);
  const [channelFilter, setChannelFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setBackendMissing(false);
    try {
      const { quick_replies } = await getQuickReplies();
      setReplies(sortReplies(quick_replies || []));
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setReplies([]);
      } else {
        setLoadError(err?.message || t("No se pudieron cargar las respuestas rápidas."));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isSupervisor) load();
    else setLoading(false);
  }, [isSupervisor, load]);

  // Traduce cualquier fallo de mutación al mismo vocabulario que la carga: si
  // el endpoint no existe el mensaje debe explicar el despliegue pendiente, no
  // sonar a error del usuario.
  const reportMutationError = useCallback(
    (err, fallback) => {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        showToast(
          t("Esta función está en construcción."),
          "warning",
        );
      } else {
        showToast(err?.message || fallback, false);
      }
    },
    [t],
  );

  const openCreate = () => {
    setFormMode("create");
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT });
    setFormOpen(true);
  };

  const openEdit = (reply) => {
    setFormMode("edit");
    setEditingId(reply.id);
    setDraft({
      title: reply.title || "",
      body: reply.body || "",
      shortcut: reply.shortcut || "",
      category: reply.category || "",
      // El Select no ofrece 'whatsapp_unofficial': sin normalizar, editar una
      // respuesta con ese canal dejaría el desplegable en blanco.
      channel: normalizeChannel(reply.channel),
      active: reply.active !== false,
    });
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      title: draft.title.trim(),
      body: draft.body,
      shortcut: draft.shortcut.trim() || null,
      category: draft.category.trim() || null,
      channel: draft.channel || null,
      active: !!draft.active,
    };
    try {
      if (formMode === "edit") {
        const { quick_reply } = await updateQuickReply(editingId, payload);
        setReplies((prev) =>
          sortReplies(prev.map((r) => (r.id === editingId ? { ...r, ...(quick_reply || payload) } : r))),
        );
        showToast(t("Respuesta rápida actualizada."), true);
      } else {
        // sort_order al final: la nueva entrada no debe reordenar el catálogo.
        const { quick_reply } = await createQuickReply({ ...payload, sort_order: replies.length });
        if (quick_reply) setReplies((prev) => sortReplies([...prev, quick_reply]));
        else await load();
        showToast(t("Respuesta rápida creada."), true);
      }
      setFormOpen(false);
    } catch (err) {
      reportMutationError(err, t("No se pudo guardar la respuesta rápida."));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (reply, next) => {
    setBusyId(reply.id);
    const previous = replies;
    setReplies((prev) => prev.map((r) => (r.id === reply.id ? { ...r, active: next } : r)));
    try {
      await updateQuickReply(reply.id, { active: next });
    } catch (err) {
      setReplies(previous);
      reportMutationError(err, t("No se pudo cambiar el estado de la respuesta."));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteQuickReply(deleteTarget.id);
      setReplies((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      showToast(t("Respuesta rápida eliminada."), true);
      setDeleteTarget(null);
    } catch (err) {
      reportMutationError(err, t("No se pudo eliminar la respuesta rápida."));
    } finally {
      setDeleting(false);
    }
  };

  // Reordenar por botones (no drag&drop) para que sea operable con teclado sin
  // capa extra. Se persiste el sort_order de las DOS filas intercambiadas.
  // El índice es SIEMPRE el del catálogo completo, no el de la lista filtrada.
  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= replies.length) return;
    const previous = replies;
    const next = [...replies];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((r, i) => ({ ...r, sort_order: i }));
    setReplies(renumbered);
    setBusyId(renumbered[target].id);
    try {
      await Promise.all([
        updateQuickReply(renumbered[index].id, { sort_order: index }),
        updateQuickReply(renumbered[target].id, { sort_order: target }),
      ]);
    } catch (err) {
      setReplies(previous);
      reportMutationError(err, t("No se pudo reordenar la respuesta rápida."));
    } finally {
      setBusyId(null);
    }
  };

  // ── Métricas del catálogo ──
  const stats = useMemo(() => {
    const active = replies.filter((r) => r.active !== false).length;
    const withShortcut = replies.filter((r) => !!r.shortcut).length;
    const channels = new Set(
      replies.map((r) => normalizeChannel(r.channel)).filter((c) => c !== ""),
    );
    return { total: replies.length, active, withShortcut, channels: channels.size };
  }, [replies]);

  // ── Chips de canal: solo los grupos que existen, con su recuento ──
  const channelGroups = useMemo(() => {
    const counts = new Map();
    replies.forEach((r) => {
      const key = normalizeChannel(r.channel);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const rank = (key) => {
      const i = CHANNEL_ORDER.indexOf(key);
      return i === -1 ? CHANNEL_ORDER.length : i;
    };
    return [...counts.entries()]
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => rank(a.key) - rank(b.key));
  }, [replies]);

  // Si el canal filtrado deja de existir (se borró la última respuesta de ese
  // canal), el filtro volvería una lista vacía sin explicación: se resetea.
  useEffect(() => {
    if (channelFilter === "all") return;
    if (!channelGroups.some((g) => g.key === channelFilter)) setChannelFilter("all");
  }, [channelGroups, channelFilter]);

  const trimmedQuery = query.trim();
  const filtersActive = channelFilter !== "all" || trimmedQuery !== "";

  const visible = useMemo(() => {
    const q = normalizeQuery(trimmedQuery);
    return replies.filter((reply) => {
      if (channelFilter !== "all" && normalizeChannel(reply.channel) !== channelFilter) return false;
      if (!q) return true;
      return [reply.title, toPlainText(reply.body), reply.shortcut, reply.category].some((field) =>
        normalizeQuery(field).includes(q),
      );
    });
  }, [replies, channelFilter, trimmedQuery]);

  const clearFilters = () => {
    setChannelFilter("all");
    setQuery("");
  };

  // ── Sin permiso ──
  if (!isSupervisor) {
    return (
      <HelpdeskSuiteLayout activeKey="suite.quickReplies">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-5 py-5">
            <EmptyState
              icon={<ShieldAlert aria-hidden="true" />}
              title={t("No tienes acceso a esta configuración")}
              description={t(
                "Las respuestas rápidas las administran supervisores y administradores. Pídele a tu supervisor que cree o edite las plantillas que necesitas.",
              )}
            />
          </div>
        </div>
      </HelpdeskSuiteLayout>
    );
  }

  const hasCatalog = !loading && !loadError && replies.length > 0;
  const showEmpty = !loading && !loadError && replies.length === 0;
  // Las métricas también se muestran con el catálogo vacío (todo en 0): sin
  // backend desplegado la vista se quedaba sin la fila superior y el layout
  // saltaba al llegar la primera respuesta. Con ceros se lee qué va a medir.
  // Solo se ocultan cuando la carga falló de verdad, porque entonces los
  // números serían mentira.
  const showStats = !loading && !loadError;

  return (
    <HelpdeskSuiteLayout activeKey="suite.quickReplies">
      <div className="flex flex-col h-full min-h-0 bg-bg">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-5 py-5 space-y-5">
            <PageHeader
              icon={
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                  <MessageSquareQuote className="w-5 h-5" aria-hidden="true" />
                </span>
              }
              title={t("Respuestas rápidas")}
              subtitle={t(
                "Plantillas que tus agentes insertan en el chat con un atajo, para responder lo de siempre sin volver a escribirlo.",
              )}
              actions={
                <>
                  <Button
                    variant="outline"
                    onClick={load}
                    disabled={loading}
                    iconLeft={
                      <RefreshCw
                        className={cn("w-4 h-4", loading && "animate-spin motion-reduce:animate-none")}
                        aria-hidden="true"
                      />
                    }
                  >
                    {t("Actualizar")}
                  </Button>
                  <Button className="hd-brand-solid" iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />} onClick={openCreate}>
                    {t("Nueva respuesta rápida")}
                  </Button>
                </>
              }
            />

            {backendMissing && (
              <PendingBackendNotice>
                {t(
                  "Las respuestas rápidas están en construcción. Puedes recorrer la configuración, pero los cambios no se guardarán hasta que esté disponible.",
                )}
              </PendingBackendNotice>
            )}

            {/* ── Métricas ── */}
            {loading && (
              <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-[104px] rounded-xl" />
                ))}
              </div>
            )}

            {/* A6 · En móvil las cuatro tarjetas de KPI llenaban los 844px del
                fold y empujaban el catálogo entero —lo único accionable— fuera
                de pantalla. Bajo 768px se colapsan en una línea de resumen. */}
            {showStats && (
              <p className="md:hidden text-xs text-fg-muted">
                {`${stats.total} ${stats.total === 1 ? t("respuesta") : t("respuestas")} · ${stats.active} ${stats.active === 1 ? t("activa") : t("activas")} · ${stats.withShortcut} ${t("con atajo")} · ${stats.channels} ${stats.channels === 1 ? t("canal") : t("canales")}`}
              </p>
            )}

            {showStats && (
              <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat
                  label={t("Respuestas")}
                  value={stats.total}
                  hint={t("en el catálogo")}
                  icon={<MessageSquareQuote aria-hidden="true" />}
                  tone="brand"
                />
                <Stat
                  label={t("Activas")}
                  value={stats.active}
                  hint={t("visibles para los agentes")}
                  icon={<Zap aria-hidden="true" />}
                  tone="success"
                />
                <Stat
                  label={t("Con atajo")}
                  value={stats.withShortcut}
                  hint={t("se insertan escribiendo /")}
                  icon={<CornerDownLeft aria-hidden="true" />}
                  tone="info"
                />
                <Stat
                  label={t("Canales")}
                  value={stats.channels}
                  hint={t("con respuestas propias")}
                  icon={<MessagesSquare aria-hidden="true" />}
                  tone="neutral"
                />
              </div>
            )}

            {/* ── Filtros ── */}
            {hasCatalog && (
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                {channelGroups.length > 1 ? (
                  <div
                    role="group"
                    aria-label={t("Filtrar por canal")}
                    className="flex flex-wrap items-center gap-1 rounded-lg border border-border bg-bg p-1"
                  >
                    <FilterChip
                      active={channelFilter === "all"}
                      onClick={() => setChannelFilter("all")}
                      label={t("Todas")}
                      count={replies.length}
                    />
                    {channelGroups.map((group) => (
                      <FilterChip
                        key={group.key || "any"}
                        active={channelFilter === group.key}
                        onClick={() => setChannelFilter(group.key)}
                        icon={channelIconFor(group.key)}
                        label={group.key === "" ? t("Todos los canales") : channelLabel(group.key, t)}
                        count={group.count}
                      />
                    ))}
                  </div>
                ) : (
                  <span />
                )}

                <div className="relative lg:w-72 shrink-0">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-subtle pointer-events-none"
                    aria-hidden="true"
                  />
                  <Input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Buscar por título, mensaje o atajo…")}
                    aria-label={t("Buscar respuestas rápidas")}
                    // La cruz nativa de WebKit se oculta: ya hay un botón de
                    // limpiar propio, tokenizado y con aria-label.
                    className="pl-9 pr-10 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
                  />
                  {trimmedQuery && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      aria-label={t("Limpiar búsqueda")}
                      className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex w-8 h-8 items-center justify-center rounded-md text-fg-subtle hover:text-fg hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── Catálogo ── */}
            {loading && (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-[92px] rounded-xl" />
                ))}
              </div>
            )}

            {loadError && (
              <div className="rounded-xl border border-border bg-bg-muted p-6 text-center">
                <AlertTriangle className="w-6 h-6 mx-auto text-status-error mb-2" aria-hidden="true" />
                <p className="text-sm font-medium text-fg">
                  {t("No se pudieron cargar las respuestas rápidas.")}
                </p>
                <p className="text-sm text-fg-muted mt-1">{loadError}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={load}>
                  {t("Reintentar")}
                </Button>
              </div>
            )}

            {showEmpty && (
              <div className="rounded-xl border border-border bg-bg">
                <EmptyState
                  icon={<MessageSquareQuote aria-hidden="true" />}
                  title={t("Todavía no hay respuestas rápidas")}
                  description={t(
                    "Una respuesta rápida es un mensaje guardado —el horario de atención, cómo se cobra un reenvío, qué pasa si nadie recibe— que el agente inserta escribiendo su atajo en el chat. Menos tipeo, misma respuesta para todo el equipo.",
                  )}
                  action={
                    <Button
                      className="hd-brand-solid"
                      iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                      onClick={openCreate}
                    >
                      {t("Crear la primera")}
                    </Button>
                  }
                />
              </div>
            )}

            {hasCatalog && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h2 className="text-sm font-semibold text-fg">{t("Catálogo")}</h2>
                  <p className="text-xs text-fg-muted">
                    {filtersActive
                      ? t("El orden se edita sin filtros activos.")
                      : t("El orden de esta lista es el que ven los agentes al buscar una plantilla.")}
                  </p>
                </div>

                <p className="sr-only" role="status" aria-live="polite">
                  {t("Respuestas visibles")}: {visible.length}
                </p>

                {visible.length === 0 ? (
                  <div className="rounded-xl border border-border bg-bg">
                    <EmptyState
                      icon={<Search aria-hidden="true" />}
                      title={t("Ninguna respuesta coincide")}
                      description={t(
                        "Prueba con otras palabras o vuelve a ver todos los canales del catálogo.",
                      )}
                      action={
                        <Button variant="outline" onClick={clearFilters}>
                          {t("Limpiar filtros")}
                        </Button>
                      }
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visible.map((reply) => {
                      const index = replies.findIndex((r) => r.id === reply.id);
                      return (
                        <QuickReplyCard
                          key={reply.id}
                          reply={reply}
                          index={index}
                          total={replies.length}
                          expanded={expandedId === reply.id}
                          onToggle={() =>
                            setExpandedId((prev) => (prev === reply.id ? null : reply.id))
                          }
                          busy={busyId === reply.id}
                          reorderLocked={filtersActive}
                          onEdit={openEdit}
                          onMove={handleMove}
                          onToggleActive={handleToggleActive}
                          onDelete={setDeleteTarget}
                          t={t}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Ayuda ── */}
            {!loading && (
              <Card padding="md">
                <div className="flex items-start gap-3">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 shrink-0">
                    <Zap className="w-4 h-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 space-y-2">
                    <h2 className="text-sm font-semibold text-fg">
                      {t("Cómo las usan los agentes")}
                    </h2>
                    <p className="text-sm text-fg-muted">
                      {t(
                        "En la conversación, el agente escribe / en el redactor y se abre el listado de respuestas rápidas: elige una y el mensaje queda escrito, listo para revisarlo antes de enviarlo.",
                      )}
                    </p>
                    <p className="text-sm text-fg-muted">
                      {t(
                        "Solo se le ofrecen las respuestas activas y las del canal de esa conversación, en el mismo orden de este catálogo.",
                      )}
                    </p>
                    <p className="flex flex-wrap items-center gap-1.5 text-xs text-fg-subtle">
                      <CornerDownLeft className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      {t("Ejemplo de atajo:")}
                      <span className="font-mono text-xs text-fg-muted rounded bg-bg-muted border border-border px-1.5 py-0.5">
                        /horario-atencion
                      </span>
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      <QuickReplyFormModal
        open={formOpen}
        mode={formMode}
        draft={draft}
        onChange={setDraft}
        onSubmit={handleSubmit}
        onClose={() => setFormOpen(false)}
        saving={saving}
        backendMissing={backendMissing}
      />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title={t("Eliminar respuesta rápida")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              {t("Eliminar")}
            </Button>
          </>
        }
      >
        {/* Título en su propio bloque: partir la frase en dos t() dejaría una
            clave sin sentido para el traductor. */}
        <p className="text-sm text-fg">{t("Vas a eliminar esta respuesta rápida:")}</p>
        <p className="mt-1 text-sm font-semibold text-fg break-words">{deleteTarget?.title}</p>
        <p className="text-sm text-fg-muted mt-3">
          {t(
            "Dejará de estar disponible para los agentes en el chat y su atajo quedará libre. Esta acción no se puede deshacer.",
          )}
        </p>
        {deleteTarget?.shortcut && (
          <p className="mt-3 font-mono text-xs text-fg-muted">{deleteTarget.shortcut}</p>
        )}
      </Modal>
    </HelpdeskSuiteLayout>
  );
}

// ─── Chip de filtro (segmented control) ──────────────────────────────────────
function FilterChip({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 h-11 md:h-8 px-3 rounded-md text-sm font-medium",
        "transition-colors duration-150 ease-out motion-reduce:transition-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "hd-brand-solid bg-brand-500 text-white" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
      )}
    >
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
      {label}
      <span className={cn("tabular-nums text-xs", active ? "text-white/80" : "text-fg-subtle")}>
        {count}
      </span>
    </button>
  );
}
